import { useAppDispatch, useAppSelector } from "@common/reducers/hooks/store.hook";
import { setDueDate, setIsRecurring, setRruleset, markDueDateSetByUser } from "@common/reducers/ScheduledMessageEditorReducer";
import { RRule } from "rrule";
import { getRuleOptionsSafe, isDateValidForRecurrence, ruleTimeBaseFor, stampRuleTimeBase, resolveStampingTzid } from "./recurrence-utils";
import { selectCurrentWorkspace } from "@common/reducers/UserSelectors";
import moment from "moment";
import { useMemo, useCallback, useRef, useEffect, type ReactNode } from "react";
import BTDatePicker from "@extension/context/content/components/bt-bulk-dashboard/time-and-date/bt-date-picker";
import { THEMED_DATE_PICKER_COLORS } from "@extension/context/content/components/bt-bulk-dashboard/time-and-date/picker-colors";

const ScheduleDatePicker = ({ trigger }: { trigger?: ReactNode } = {}) => {
    const dispatch = useAppDispatch();
    const dueDate = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.dueDate);
    const rruleset = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.rruleset);
    const isRecurring = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.isRecurring);
    // BE#648 + FE#5.9.28: re-anchoring a recurring series from the date picker writes
    // the rule in `effectiveTz` + LOCAL wall-clock (DTSTART;TZID), consistent with the
    // stored dueDate. An existing rule's OWN TZID wins; a brand-new rule is seeded from
    // the VIEWER's browser zone first (→ workspace tz → legacy UTC/no-TZID).
    const workspaceTimezone = useAppSelector(selectCurrentWorkspace)?.timezone;
    const effectiveTz = resolveStampingTzid(rruleset, workspaceTimezone);

    const ruleOptions = useMemo(
        () => getRuleOptionsSafe(rruleset, dueDate, effectiveTz),
        [dueDate, rruleset, effectiveTz]
    );

    // Create disabled function for dates that don't match recurrence pattern
    const disabledDateFunction = useMemo(() => {
        if (!isRecurring || !rruleset) {
            return undefined; // No restrictions when not recurring
        }
        
        return (date: Date): boolean => {
            // Return true to disable dates that don't match the recurrence
            return !isDateValidForRecurrence(date, rruleset, dueDate);
        };
    }, [isRecurring, rruleset, dueDate]);

    // Track last value sent to prevent loops
    const lastSentValueRef = useRef<string>('');

    // Convert ISO date to dd/mm/yyyy format (using local time for display)
    const dateValue = useMemo(() => {
        if (!dueDate) return '';
        try {
            const m = moment.utc(dueDate);
            if (!m.isValid()) return '';
            const localM = m.local();
            const day = String(localM.date()).padStart(2, '0');
            const month = String(localM.month() + 1).padStart(2, '0');
            const year = localM.year();
            return `${day}/${month}/${year}`;
        } catch {
            return '';
        }
    }, [dueDate]);

    // BE#657 — do NOT auto-seed an empty dueDate to "now". A near-now dueDate makes an
    // unfinished scheduled draft immediately sendable, so if it is persisted before the
    // user picks the intended (future) date the scheduler fires it early. The compose
    // Send gate (canSchedule) already requires an explicit valid, non-past date, so an
    // empty dueDate simply keeps Send disabled until the user picks one. The "set it to
    // now" quick action (footer handleSetToNow) remains the explicit way to send now.

    // Sync ref when value changes externally (from Redux) - but only if different
    useEffect(() => {
        if (dateValue && dateValue !== lastSentValueRef.current) {
            lastSentValueRef.current = dateValue;
        }
    }, [dateValue]);

    const handleDateChange = useCallback((dateString: string) => {
        // Prevent loops - if this is the same value we just sent, ignore it
        if (dateString === lastSentValueRef.current) {
            // BE#657 M2 — but still treat accepting the shown date as an explicit pick,
            // so a user who confirms a pre-seeded date can enable Send without changing it.
            dispatch(markDueDateSetByUser());
            return;
        }
        
        // dateString is in dd/mm/yyyy format
        const parts = dateString.split('/');
        if (parts.length !== 3) return;
        
        const [day, month, year] = parts.map(Number);
        if (isNaN(day) || isNaN(month) || isNaN(year)) return;
        
        // Get current time from existing dueDate, or use current time
        const existingMoment = dueDate ? moment.utc(dueDate) : moment();
        const existingLocal = existingMoment.local();
        
        // Create new date with preserved time in local timezone
        const newLocalMoment = moment()
            .year(year)
            .month(month - 1)
            .date(day)
            .hour(existingLocal.hour())
            .minute(existingLocal.minute())
            .second(0)
            .millisecond(0);
        
        if (!newLocalMoment.isValid()) return;
        
        // Convert to UTC for storage
        const newDueDateString = newLocalMoment.utc().toISOString();
        
        // Update ref before dispatching
        lastSentValueRef.current = dateString;
        
        try {
            // If recurrence is active, validate the selected date matches the pattern
            if (isRecurring && rruleset) {
                const testDate = newLocalMoment.toDate();
                const isValid = isDateValidForRecurrence(testDate, rruleset, dueDate);
                
                if (!isValid) {
                    // This shouldn't happen if disabled properly, but as a safety measure,
                    // find the closest valid occurrence
                    console.warn("Selected date doesn't match recurrence pattern, finding closest valid occurrence");
                    
                    try {
                        const rule = new RRule({
                            ...ruleOptions,
                            dtstart: moment.utc(dueDate).startOf("minute").toDate(),
                        });
                        
                        // Find the next valid occurrence after the selected date
                        const nextOccurrence = rule.after(testDate);
                        if (nextOccurrence) {
                            const nextMoment = moment(nextOccurrence);
                            const adjustedMoment = moment()
                                .year(nextMoment.year())
                                .month(nextMoment.month())
                                .date(nextMoment.date())
                                .hour(existingLocal.hour())
                                .minute(existingLocal.minute())
                                .second(0)
                                .millisecond(0);
                            
                            const adjustedDueDateString = adjustedMoment.utc().toISOString();
                            const adjustedRrule = new RRule(
                                stampRuleTimeBase(ruleOptions, ruleTimeBaseFor(adjustedDueDateString, effectiveTz)),
                            );
                            dispatch(setRruleset(adjustedRrule.toString()));
                            dispatch(setDueDate(adjustedDueDateString));
                            return;
                        }
                    } catch (ruleError) {
                        console.error("Failed to find valid occurrence:", ruleError);
                    }
                } else {
                    // Date is valid, update both dueDate and dtstart. Stamp the rule in
                    // the workspace zone + wall-clock, consistent with the stored dueDate.
                    const rrule = new RRule(
                        stampRuleTimeBase(ruleOptions, ruleTimeBaseFor(newDueDateString, effectiveTz)),
                    );
                    dispatch(setRruleset(rrule.toString()));
                }
            }
            dispatch(setDueDate(newDueDateString));
        } catch (error) {
            console.error("Failed to update date:", error);
            dispatch(setDueDate(newDueDateString));
            if (isRecurring) {
                dispatch(setRruleset(''));
                dispatch(setIsRecurring(false));
            }
        }
    }, [dueDate, dispatch, ruleOptions, isRecurring, rruleset, effectiveTz]);

    // CSS variables, not resolved colours: the stylesheet re-resolves them the
    // moment the theme flips. See picker-colors.ts for why the old one-time read
    // of `<html>` was always the light value.
    return (
        <BTDatePicker
            value={dateValue}
            onChange={handleDateChange}
            {...THEMED_DATE_PICKER_COLORS}
            disabled={disabledDateFunction}
            trigger={trigger}
            seedDefaultOnMount={false}
        />
    );
};

export default ScheduleDatePicker;